import { useEffect, useState, useMemo } from "react";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import { getTotalOrderValueColumns } from "../utils/utils";
import {
  setClaimSummaryRows,
  setTotalOrderValueRows,
  setSelectedClaimSubType,
  setSelectedClaimType,
  setTotalOrderValueCheckedClaimIds,
  setMonth,
  setSearchText,
  setClaimStartDate,
  setClaimEndDate,
} from "../../../stateManagement/actions/claims";
import { useSelector, useDispatch } from "react-redux";
import MiniClaimSummary from "../components/MiniClaimSummary";
import { Loader } from "../../../components/loader/Loader";
import dayjs from "dayjs";
import { getLast12Months } from "../../../utils/calenderUtils";
import { getUTCDate } from "../../../services/manageOrderService";
import { getTovInvoices, getClaimDetailsByReference } from "../../../services/claimService";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";
import "./PurchaseReturns.css";
import { logLoadTrace } from "../../../../src/firebase";

const TotalOrderValue: React.FC = () => {
  const { totalOrderValueRows, totalOrderValueCheckedClaimIds, month, searchText, claimStartDate, claimEndDate } = useSelector(
    (state: any) => state.claims
  );
  const loginId = JSON.parse(localStorage.authContext)?.user?.loginId;
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);

  const filteredRows = useMemo(() => {
    const term = searchText.toLowerCase().replace(/\s/g, '');
    if (!term) return totalOrderValueRows;

    return totalOrderValueRows.filter((row: any) => {
      const matches = [row.invoiceNo, row.outletNameCode].some(field =>
        field?.toLowerCase().replace(/\s/g, '').includes(term));
      return matches;
    });
  }, [searchText, totalOrderValueRows]);

  const visibleCheckedIds = useMemo(() => {
    return totalOrderValueCheckedClaimIds.filter((id: any) =>
      filteredRows.some((row: any) => row.id === id)
    );
  }, [totalOrderValueCheckedClaimIds, filteredRows]);

  const fetchData = async () => {
    setLoader(true);
    try {
      let formattedStartDate: string;
      let formattedEndDate: string;

      if (claimStartDate && claimEndDate) {
        const start = dayjs(claimStartDate);
        const end = dayjs(claimEndDate);
        formattedStartDate = getUTCDate(start, "start");
        formattedEndDate = getUTCDate(end, "end");
      } else {
        if (!month) return;
        const monthDate = dayjs(month, "MMM YYYY");
        formattedStartDate = getUTCDate(monthDate.startOf("month"), "start");
        formattedEndDate = getUTCDate(monthDate.endOf("month"), "end");
      }

      const [tovInvoicesResponse] = await Promise.all([
        getTovInvoices(formattedStartDate, formattedEndDate)
      ]);

      const tovInvoicesData = tovInvoicesResponse || [];
      const allInvoiceNumbers = Array.from(new Set(
        tovInvoicesData
          .map((invoice: any) => invoice.inv_ser_no ? invoice.inv_ser_no : invoice.invoice_number)
          .filter(Boolean)
      ));

      let claimsData: any[] = [];
      if (allInvoiceNumbers.length > 0) {
        try {
          const response = await getClaimDetailsByReference(allInvoiceNumbers);
          claimsData = response || [];
        } catch (error) {
          console.error("Error fetching claim details:", error);
          claimsData = [];
        }
      }

      const claimedInvoiceMap: Record<string, { status: string, approvedAmount: number }> = {};
      claimsData.forEach((detail: any) => {
          if (detail.referenceNumber) {
            if (detail.status === 'APPROVED' ||
              detail.status === 'REQUESTED' ||
              detail.status === 'DRAFT' ||
              (detail.status === 'PARTIALLY_APPROVED' && detail.approvedAmount > 0)) {
              claimedInvoiceMap[detail.referenceNumber] = {
                status: detail.status,
                approvedAmount: detail.approvedAmount
              };
            }
          }
        });

      const unclaimedInvoices = tovInvoicesData.filter((invoice: any) => {
        const invoiceNumber = invoice.inv_ser_no ? invoice.inv_ser_no : invoice.invoice_number;
        return !claimedInvoiceMap[invoiceNumber] ||
          (claimedInvoiceMap[invoiceNumber].status !== 'APPROVED' &&
            claimedInvoiceMap[invoiceNumber].status !== 'REQUESTED' &&
            claimedInvoiceMap[invoiceNumber].status !== 'DRAFT' &&
            !(claimedInvoiceMap[invoiceNumber].status === 'PARTIALLY_APPROVED' &&
              claimedInvoiceMap[invoiceNumber].approvedAmount > 0));
      });

      const rows = unclaimedInvoices.map((invoice: any, index: number) => ({
        id: `${invoice.inv_ser_no ? invoice.inv_ser_no : invoice.invoice_number}-${index}`,
        invoiceNo: invoice.inv_ser_no ? invoice.inv_ser_no : invoice.invoice_number,
        invoiceDateTime: invoice.creation_time,
        outletNameCode: `${invoice.outletName || "N/A"} - ${invoice.outletcode || "N/A"}`,
        cases: invoice.totalCaseQuantity,
        pieces: invoice.totalPieceQuantity,
        others: invoice.totalOtherUnitQuantity,
        totalAmount: invoice.total_amount,
        orderNo: `${invoice.order_number}-${loginId}`,
        discount: parseFloat(formatDecimal(Number(invoice.discount_info?.discount || invoice.discount_info?.finalBenefit))) || 0,
        showEyeIcon: true,
      }));
      dispatch(setTotalOrderValueRows(rows || []));

      const previousCheckedIds = totalOrderValueCheckedClaimIds;
      const filteredCheckedIds = previousCheckedIds.filter((id: any) =>
        rows.some(row => row.id === id)
      );
      dispatch(setTotalOrderValueCheckedClaimIds(
        filteredCheckedIds.length ? filteredCheckedIds :
          rows.filter(row => row.discount !== 0).map(row => row.id)
      ));

    } catch (error) {
      console.error("Error fetching TOV invoices:", error);
    } finally {
      setLoader(false);
    }
  };

  const init = async () => {
    setLoader(true);
    await Promise.allSettled([fetchData()]).finally(
      () => {
        setLoader(false);
      }
    )
  }

  useEffect(() => {
    const hasRange = claimStartDate && claimEndDate;
    if (hasRange || month) {
      logLoadTrace(init, "claims_tov_secondary_schemes_page_load_time");
    }
  }, [month, claimStartDate, claimEndDate]);

  useEffect(() => {
    const updatedClaimSummaryRows = totalOrderValueRows?.filter((obj: any) =>
      totalOrderValueCheckedClaimIds.includes(obj.id)
    );
    dispatch(setClaimSummaryRows(updatedClaimSummaryRows));
  }, [totalOrderValueCheckedClaimIds, totalOrderValueRows]);

  useEffect(() => {
    dispatch(setSelectedClaimType("Secondary Schemes"));
    dispatch(setSelectedClaimSubType("Total Order Value"));
  }, []);

  const handleSetCheckedIdArray = (ids: any[]) => {
    const validIds = ids.filter((id) => {
      const row = totalOrderValueRows.find((row: any) => row.id === id);
      return row && row.discount !== 0;
    });
    dispatch(setTotalOrderValueCheckedClaimIds(validIds));
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes("totalOrderValue")) {
        dispatch(setSearchText(""));
        dispatch(setTotalOrderValueCheckedClaimIds([]));
        const currentMonth = getLast12Months()[0];
        dispatch(setMonth(currentMonth));
        dispatch(setClaimStartDate(null));
        dispatch(setClaimEndDate(null));
        dispatch(setSelectedClaimSubType(""));
      }
    };
  }, []);

  return (
    <div className="claims_table_container">
      {loader ? (
        <Loader />
      ) : (
        <>
          <MiniClaimSummary rows={totalOrderValueRows} />
          <CustomDataGrid
            columnsMetaData={getTotalOrderValueColumns()}
            rows={filteredRows}
            isCheckboxSelection={true}
            insideSecondarySchemes={true}
            redirectUrl="/claims/create/totalOrderValue/invoiceDetails/"
            checkedIdArray={visibleCheckedIds}
            setCheckedIdArray={handleSetCheckedIdArray}
          />
        </>
      )}
    </div>
  );
};
export default TotalOrderValue;
